import React, { CSSProperties, useLayoutEffect, useRef, useState } from 'react';
import { DataGrid } from '@biq/apex-ui';
import { Grid, SxProps } from '@mui/material';
import {
    GridFilterModel,
    GridPaginationModel,
    GridRowHeightReturnValue,
    GridSortModel,
    GridState,
} from '@mui/x-data-grid';
import { EmptyStateContainer } from 'components/empty-state-container/EmptyStateContainer';
import { PaginationModel } from '../../../lib';

interface TableComponentProps {
    tableData: any;
    gridHeight: any;
    tableHeight: number;
    maxGridHeight: any;
    autoRowHeight?: boolean;
    isGridHeightAuto?: boolean;
    columns: any;
    getRowId: any;
    onRowClick: (row: any) => void;
    paginationpageSizeOptions: Array<number>;
    paginationPageSize: number;
    emptyStateIcon: string;
    emptyStateHeading?: string;
    emptyStateBodyMessage: React.ReactNode;
    readonly?: boolean;
    readonlyTooltipTitle?: string;
    customDataGridStyles?: SxProps;
    customEmptyStateContainerStyles?: SxProps;
    customImageStyles?: CSSProperties;
    customHeadingContainerStyles?: SxProps;
    customHeadingTextStyles?: SxProps;
    isAccordionContainer?: boolean;
    variantType?: 'outline' | 'borderless' | 'shadow';
    sortModel?: GridSortModel;
    rowCount?: number;
    paginationMode?: 'client' | 'server';
    onPaginationModelChange?: React.Dispatch<
        React.SetStateAction<PaginationModel>
    >;
    paginationModel?: GridPaginationModel;
    handleStateChange?: (state: GridState) => void;
    filterModel?: GridFilterModel;
    onFilterModelChange?: (model: GridFilterModel) => void;
}

export const TableComponent = ({
    tableData,
    gridHeight,
    tableHeight,
    maxGridHeight,
    autoRowHeight = false,
    isGridHeightAuto = false,
    columns,
    getRowId,
    onRowClick,
    paginationpageSizeOptions,
    paginationPageSize,
    emptyStateIcon,
    emptyStateHeading,
    emptyStateBodyMessage,
    readonly = false,
    readonlyTooltipTitle = '',
    customDataGridStyles,
    customEmptyStateContainerStyles,
    customImageStyles,
    customHeadingContainerStyles,
    customHeadingTextStyles,
    isAccordionContainer = false,
    variantType = 'outline',
    sortModel,
    rowCount,
    paginationMode = 'client',
    onPaginationModelChange,
    paginationModel,
    handleStateChange,
    filterModel,
    onFilterModelChange,
}: TableComponentProps): React.JSX.Element => {
    const gridRef = useRef<HTMLDivElement>(null);
    const [isAutoHeight, setIsAutoHeight] = useState<boolean>(isGridHeightAuto);

    useLayoutEffect(() => {
        setTimeout(() => {
            if (!gridRef.current) return;

            const content = gridRef.current.querySelector(
                '.MuiDataGrid-virtualScrollerContent'
            );
            if (!content) return;

            const observer = new ResizeObserver((entries) => {
                const height = entries[0].contentRect.height;
                if (height < window.innerHeight - tableHeight) {
                    setIsAutoHeight(true);
                } else {
                    setIsAutoHeight(false);
                }
            });

            observer.observe(content);

            return () => observer.disconnect();
        }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridRef.current, tableHeight, tableData?.length]);

    return tableData?.length ? (
        <Grid
            sx={
                isAccordionContainer
                    ? {
                          height: gridHeight,
                          maxHeight: maxGridHeight,
                          width: '100%',
                      }
                    : {
                          height: gridHeight,
                          maxHeight: maxGridHeight,
                          width: '100%',
                          mt: 2.5,
                      }
            }
            ref={gridRef}
        >
            <DataGrid
                sx={{ ...customDataGridStyles }}
                variantType={variantType}
                autoHeight={isAutoHeight}
                getRowHeight={
                    autoRowHeight
                        ? (): GridRowHeightReturnValue => 'auto'
                        : undefined
                }
                rows={tableData}
                columns={columns}
                getRowId={getRowId}
                disableRowSelectionOnClick
                onRowClick={onRowClick}
                paginationModel={paginationModel}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: paginationPageSize,
                        },
                    },
                    sorting: {
                        sortModel: sortModel,
                    },
                }}
                pageSizeOptions={paginationpageSizeOptions}
                pagination
                rowCount={rowCount}
                paginationMode={paginationMode}
                onPaginationModelChange={onPaginationModelChange}
                readonly={readonly}
                readonlyTooltipTitle={readonlyTooltipTitle}
                disableVirtualization={true}
                onStateChange={handleStateChange}
                filterModel={filterModel}
                onFilterModelChange={onFilterModelChange}
            />
        </Grid>
    ) : (
        <EmptyStateContainer
            customEmptyStateContainerStyles={customEmptyStateContainerStyles}
            customImageStyles={customImageStyles}
            emptyStateIcon={emptyStateIcon}
            customHeadingContainerStyles={customHeadingContainerStyles}
            customHeadingTextStyles={customHeadingTextStyles}
            emptyStateHeading={emptyStateHeading}
            emptyStateBodyMessage={emptyStateBodyMessage}
        />
    );
};
